import { useSolidChip } from '@/hooks/use-theme-colors';
import { AppearanceCard } from '@/components/profile/appearance-card';
import React, { useRef, useState } from 'react';
import {
  View,
  Image,
  Pressable,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  TextInput,
  StyleSheet,
} from 'react-native';
import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';

import { ThemedText } from '@/components/themed-text';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChoiceDialog } from '@/components/ui/choice-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useProviderProfile } from '@/hooks/use-provider';
import { useMyPublicProfile } from '@/hooks/use-public-profile';
import { useProviderEnrollments } from '@/hooks/use-enrollments';
import { canManagePaymentAccount, useMembership } from '@/hooks/use-membership';
import { useTerms } from '@/hooks/use-terms';
import api from '@/services/api';
import { getApiHostHeaders, requireApiBaseUrl } from '@/lib/api-url';
import { appendCompressedImage } from '@/lib/image-upload';
import { USER_ROLE } from '@/types/user-role';
import {
  isProviderUser,
  hasClientProfile,
  hasProviderProfile,
  // hasDualProfile, // desativado: prestador poder ser cliente (front não espera mais dual profile; prestador sempre prevalece)
} from '@/utils/user-role';
import { AddCredentialsModal } from '@/components/auth/add-credentials-modal';
import { AddWhatsappModal } from '@/components/auth/add-whatsapp-modal';
import { PaymentAccountEntryCard } from '@/components/payments/payment-account-entry-card';
import { EditProfileModal } from './edit-profile-modal';
// import { AddClientProfileModal } from './add-client-profile-modal'; // desativado: prestador poder ser cliente
import { WhatsappTemplatesManager } from './whatsapp-templates-manager';
// import { WhatsappConnectCard } from './whatsapp-connect-card'; // desativado: envio sai pelo número da ControlaPag
import { router } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getFetchErrorMessage, getApiErrorMessage } from '@/utils/api-error';

const DEFAULT_BRAND_COLOR = '#FD5001';

// Helper para formatar data
const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

// Stats Card Component
function StatCard({
  icon,
  label,
  value,
  color = 'primary',
  delay = 0,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string | number;
  color?: 'primary' | 'success' | 'warning';
  delay?: number;
}) {
  const solid = useSolidChip();
  const colorClasses = {
    primary: 'bg-primary/10 dark:bg-primary',
    success: 'bg-success-100 dark:bg-success-500',
    warning: 'bg-warning-100 dark:bg-warning-500',
  };

  const iconColors = {
    primary: '#FD5001',
    success: '#22c55e',
    warning: '#f59e0b',
  };

  return (
    <Animated.View
      entering={FadeInDown.delay(delay).duration(400)}
      className="min-w-[140px] flex-1"
      style={
        Platform.OS === 'web'
          ? ({ flexGrow: 1, flexBasis: 'calc(50% - 12px)', minWidth: 180 } as const)
          : undefined
      }
    >
      {/* flex-1: estica até o card mais alto da linha */}
      <Card className="flex-1 overflow-hidden">
        <CardContent className="p-4">
          <View
            className={`h-10 w-10 rounded-xl ${colorClasses[color]} mb-3 items-center justify-center`}
          >
            <Ionicons name={icon} size={20} color={solid.icon(iconColors[color])} />
          </View>
          <ThemedText className="font-bold text-2xl text-foreground">{value}</ThemedText>
          <ThemedText className="mt-1 text-sm text-foreground/60">{label}</ThemedText>
        </CardContent>
      </Card>
    </Animated.View>
  );
}

// Tile de navegação no mesmo estilo dos StatCards (ícone + título + descrição)
function ProfileNavTile({
  icon,
  title,
  subtitle,
  onPress,
  isWeb,
  delay = 0,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  subtitle: string;
  onPress: () => void;
  isWeb: boolean;
  delay?: number;
}) {
  return (
    <Animated.View
      entering={FadeInDown.delay(delay).duration(400)}
      className="min-w-[140px] flex-1"
      style={
        isWeb ? ({ flexGrow: 1, flexBasis: 'calc(50% - 12px)', minWidth: 180 } as const) : undefined
      }
    >
      {/* flex-1: estica até o card mais alto da linha */}
      <Card className="flex-1 overflow-hidden">
        <Pressable
          onPress={onPress}
          className="flex-1 p-4"
          style={isWeb ? ({ cursor: 'pointer' } as const) : undefined}
        >
          <View className="mb-3 flex-row items-center justify-between">
            <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
              <Ionicons className="text-primary dark:text-white" name={icon} size={20} />
            </View>
            <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
          </View>
          <ThemedText className="font-bold text-base text-foreground">{title}</ThemedText>
          <ThemedText className="mt-1 text-sm text-foreground/60" numberOfLines={2}>
            {subtitle}
          </ThemedText>
        </Pressable>
      </Card>
    </Animated.View>
  );
}

// Info Row Component
function InfoRow({
  icon,
  label,
  value,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string | undefined;
}) {
  return (
    <View
      className={`flex-row items-start gap-4 border-b border-border/50 py-4 last:border-b-0 ${Platform.OS === 'web' ? 'py-5' : ''}`}
    >
      <View className="h-10 w-10 items-center justify-center rounded-xl bg-neutral-100">
        <Ionicons className="text-neutral-500" name={icon} size={20} />
      </View>
      <View className="flex-1">
        <ThemedText className="mb-1 text-xs uppercase tracking-wider text-foreground/50">
          {label}
        </ThemedText>
        <ThemedText className="text-base text-foreground" numberOfLines={2}>
          {value || 'Não informado'}
        </ThemedText>
      </View>
    </View>
  );
}

const AVATAR_SIZE = 112;

export function ProfileScreen() {
  const { user, setUser, deleteAccount } = useAuthStore();
  const { addToast } = useToastStore();
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isMobile = width < 768;
  const isNativeMobile = Platform.OS !== 'web';
  const isWeb = Platform.OS === 'web';
  const profileSectionGap = isWeb ? 'mt-10' : 'mt-6';
  const profileStatsGap = isWeb ? 'gap-3' : 'gap-2';
  const profileStatsMb = isWeb ? '' : 'mb-2';
  const profileFooterGap = isWeb ? 'mt-12' : 'mt-8';
  const webSectionStackStyle = isWeb
    ? ({ display: 'flex', flexDirection: 'column', gap: 40, width: '100%' } as const)
    : undefined;
  const webStatsRowStyle = isWeb
    ? ({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 12, width: '100%' } as const)
    : undefined;
  // Bloco dos 4 cards do topo (stats + tiles): junto no web, sem os 40px do stack
  const webTopBlockStyle = isWeb
    ? ({ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' } as const)
    : undefined;

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  // const [showAddClientProfileModal, setShowAddClientProfileModal] = useState(false); // desativado: prestador poder ser cliente
  const [whatsappTestPhone, setWhatsappTestPhone] = useState('');
  const [whatsappTestTemplate, setWhatsappTestTemplate] = useState('hello_world');
  const [whatsappTestLoading, setWhatsappTestLoading] = useState(false);
  const [whatsappParams, setWhatsappParams] = useState<{ name: string; value: string }[]>([]);
  const [whatsappButtonParam, setWhatsappButtonParam] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [avatarSourceOpen, setAvatarSourceOpen] = useState(false);

  const isProvider = isProviderUser(user);
  // Prestador prevalece: se já é prestador, front não trata mais como cliente (mesmo com clientProfile legado no banco)
  const isClient = !isProvider && (user?.role === USER_ROLE.CLIENT || hasClientProfile(user));

  // Papel na organização: solo vê o convite para virar equipe; profissional
  // não vê plano, e Pix só quando ele mesmo recebe.
  const { isOwner, isProfessional, membership } = useMembership();
  const terms = useTerms({ enabled: isProvider });

  /**
   * O dono pode estar vendo outro profissional (seletor da sidebar). A tela
   * mistura duas coisas: o **perfil** (foto, nome, negócio, pagamento), que a
   * API já devolve do profissional em foco, e a **conta** (email, senha,
   * plano, excluir conta), que é sempre de quem está logado. Sob foco só o
   * perfil aparece — senão a tela mostraria a foto e o email do dono sob o
   * nome do profissional, e a câmera gravaria a foto na conta errada.
   */
  const acting = useActingProviderStore((s) => s.acting);
  const isActing = !!acting;
  // T7.2: `can('ownPayment')` sozinho não olha o foco (dono "agindo como"
  // outro profissional continuaria vendo `true` no modo "por profissional",
  // e a API recusaria) — `canManagePaymentAccount` é quem decide aqui.
  const canManagePayment = canManagePaymentAccount(membership, isActing);
  // Caixa único, quem vê não é o dono: nunca gerencia, mas o card ainda
  // aparece — só explica onde o dinheiro cai (T7.2, plano 4.7), em vez de
  // sumir como um `can('ownPayment')` sozinho faria.
  const isSingleTillNonOwner =
    membership?.type === 'TEAM' && membership?.modes?.paymentMode === 'ORGANIZATION' && !isOwner;

  // Buscar dados do provider se for provider
  const { data: providerData, refetch: refetchProvider } = useProviderProfile();
  // Capa/cor do perfil público: mesmo fundo configurado em /profile/public
  const { data: publicProfile } = useMyPublicProfile({ enabled: isProvider });
  const brandColor = publicProfile?.brandColor ?? DEFAULT_BRAND_COLOR;
  const coverUrl = publicProfile?.coverUrl ?? null;
  // Buscar contratos do provider para estatísticas
  const { data: enrollmentsData, refetch: refetchEnrollments } = useProviderEnrollments({
    page: 1,
    limit: 1,
  });

  const isFirstFocus = useRef(true);
  useFocusEffect(
    React.useCallback(() => {
      if (isFirstFocus.current) {
        isFirstFocus.current = false;
        return;
      }
      refetchEnrollments();
      refetchProvider();
    }, [refetchEnrollments, refetchProvider]),
  );

  /**
   * Quem cadastra pelo WhatsApp entra sem `username`: a tela mostrava
   * "Usuário" para um dono que tem nome do negócio gravado desde o
   * onboarding. O nome do prestador é o `title`; o `username` é o resto.
   */
  const displayName =
    (isProvider ? providerData?.title?.trim() : '') ||
    (isActing ? acting.name : '') ||
    user?.username ||
    '';

  /** Sob foco a foto é a do profissional (vem junto no store, sem esperar a lista). */
  const avatarImage = isActing ? acting.image : (user?.image ?? null);

  const getUserInitials = () => {
    if (!displayName) return 'U';
    const parts = displayName.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const handleDeleteAccount = () => setShowDeleteAccountModal(true);

  const confirmDeleteAccount = () => {
    setIsDeletingAccount(true);
    void deleteAccount()
      .catch(() => {})
      .finally(() => {
        setIsDeletingAccount(false);
        setShowDeleteAccountModal(false);
      });
  };

  const handleChangeAvatar = () => {
    if (isUploadingAvatar) return;
    // `POST /user/avatar` grava na conta logada, não no profissional em foco.
    if (isActing) {
      addToast(`A foto de ${acting.name} é ele mesmo quem troca, no perfil dele.`, 'error');
      return;
    }

    if (Platform.OS === 'web') {
      void pickImage();
      return;
    }

    setAvatarSourceOpen(true);
  };

  const pickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para acessar galeria é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      await uploadAvatar(result.assets[0].uri);
    }
  };

  const takePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para usar câmera é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      await uploadAvatar(result.assets[0].uri);
    }
  };

  const uploadAvatar = async (imageUri: string) => {
    setIsUploadingAvatar(true);

    try {
      const formData = new FormData();
      await appendCompressedImage(formData, 'file', imageUri, 'avatar');

      const API_URL = requireApiBaseUrl().replace(/\/$/, '');
      const token = useAuthStore.getState().token;

      const response = await fetch(`${API_URL}/user/avatar`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          ...getApiHostHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getFetchErrorMessage(response.status, errorData, 'Erro ao enviar foto.'));
      }

      const updatedUser = await response.json();

      // Atualizar o user no store
      setUser({
        ...user!,
        image: updatedUser.image,
      });

      addToast('Foto de perfil atualizada com sucesso!', 'success');
    } catch (error: any) {
      console.error('Erro no upload do avatar:', error);
      addToast(getApiErrorMessage(error, 'Erro ao enviar foto de perfil.'), 'error');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Dados específicos por tipo de usuário
  const clientProfile = user?.clientProfile;

  // Stats para exibir
  const stats = isProvider
    ? [
        {
          icon: 'briefcase-outline' as const,
          label: 'Serviços',
          value: providerData?.services?.length || 0,
          color: 'primary' as const,
        },
        {
          icon: 'document-text-outline' as const,
          label: 'Contratos Ativos',
          value: enrollmentsData?.meta?.total || 0,
          color: 'primary' as const,
        },
      ]
    : [
        {
          icon: 'document-text-outline' as const,
          label: 'Contratos',
          value: clientProfile?.enrollments?.length || 0,
          color: 'primary' as const,
        },
      ];

  return (
    <View className="flex-1 bg-canvas">
      <DashboardScrollView
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header com o mesmo fundo do perfil público (capa ou cor da marca) */}
        <View
          className={`px-6 ${isNativeMobile ? 'pt-0' : 'pb-24 pt-8'} ${Platform.OS === 'web' ? 'items-center' : ''}`}
          style={[
            { backgroundColor: brandColor, overflow: 'hidden' },
            isNativeMobile
              ? { paddingTop: insets.top + 12, paddingBottom: 40 }
              : Platform.OS === 'web'
                ? ({ width: '100%' } as const)
                : null,
          ]}
        >
          {coverUrl ? (
            <>
              <Image
                source={{ uri: coverUrl }}
                aria-hidden
                style={StyleSheet.absoluteFill}
                resizeMode="cover"
              />
              {/* Scrim: mantém avatar/nome legíveis sobre qualquer capa */}
              <LinearGradient
                colors={['rgba(0,0,0,0.25)', 'rgba(0,0,0,0.5)']}
                style={StyleSheet.absoluteFill}
              />
            </>
          ) : (
            <LinearGradient
              colors={[brandColor, `${brandColor}B3`]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
          )}
          <Animated.View
            entering={FadeIn.duration(400)}
            className="w-full items-center"
            style={
              Platform.OS === 'web'
                ? ({
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  } as const)
                : undefined
            }
          >
            {/* Avatar */}
            <View
              className="relative mb-4 shrink-0 self-center"
              style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
            >
              <Pressable
                onPress={handleChangeAvatar}
                disabled={isUploadingAvatar || isActing}
                className="h-full w-full items-center justify-center overflow-hidden rounded-full border-4 border-white/30 bg-white/20"
                style={
                  Platform.OS === 'web' && !isActing ? ({ cursor: 'pointer' } as const) : undefined
                }
              >
                {isUploadingAvatar ? (
                  <View className="h-full w-full items-center justify-center bg-black/30">
                    <ActivityIndicator size="large" color="white" />
                  </View>
                ) : avatarImage ? (
                  <Image
                    source={{ uri: avatarImage }}
                    accessibilityLabel="Sua foto de perfil"
                    className="h-full w-full"
                    resizeMode="cover"
                    style={
                      Platform.OS === 'web'
                        ? ({
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                          } as const)
                        : undefined
                    }
                  />
                ) : (
                  <ThemedText
                    className="font-bold text-white"
                    style={{
                      fontSize: 40,
                      lineHeight: 50,
                      textAlign: 'center',
                      includeFontPadding: false,
                    }}
                  >
                    {getUserInitials()}
                  </ThemedText>
                )}
              </Pressable>

              {!isUploadingAvatar && !isActing && (
                <Pressable
                  onPress={handleChangeAvatar}
                  accessibilityLabel="Alterar foto de perfil"
                  className="absolute bottom-1.5 right-1.5 h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-card shadow-md"
                  style={Platform.OS === 'web' ? ({ cursor: 'pointer' } as const) : undefined}
                >
                  <Ionicons name="camera" size={15} color="#FD5001" />
                </Pressable>
              )}
            </View>

            {/* Nome e Role */}
            <ThemedText
              className="w-full text-center font-bold text-2xl leading-tight text-white"
              style={Platform.OS === 'web' ? ({ textAlign: 'center' } as const) : undefined}
            >
              {displayName || 'Usuário'}
            </ThemedText>
            <View
              className={`mt-2 self-center rounded-full bg-white/20 px-3 py-1 ${isNativeMobile ? 'mb-2' : 'mb-1'}`}
            >
              <ThemedText className="text-center text-sm leading-tight text-white/90">
                {/* Dual profile (Prestador e Cliente) desativado: prestador sempre prevalece */}
                {isActing
                  ? `${terms.professional} · ${membership?.organizationName ?? ''}`.trim()
                  : isProvider
                    ? 'Prestador de Serviço'
                    : 'Cliente'}
              </ThemedText>
            </View>
          </Animated.View>
        </View>

        {/* Conteúdo Principal */}
        <View
          className={`px-4 ${
            isNativeMobile
              ? 'mt-6'
              : Platform.OS === 'web' && isMobile
                ? 'mt-4'
                : Platform.OS === 'web'
                  ? '-mt-20 w-full max-w-4xl self-center'
                  : '-mt-16'
          }`}
          style={webSectionStackStyle}
        >
          {/* Em foco: o que está abaixo é do profissional; a conta continua sendo a sua. */}
          {isActing && (
            <Animated.View entering={FadeInDown.duration(400)}>
              <Card>
                <CardContent className="flex-row items-center gap-3 p-4">
                  <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                    <Ionicons
                      className="text-primary dark:text-white"
                      name="eye-outline"
                      size={20}
                    />
                  </View>
                  <View className="min-w-0 flex-1">
                    <ThemedText className="font-semibold text-[15px] leading-5 text-foreground">
                      Você está vendo o perfil de {acting.name}
                    </ThemedText>
                    <ThemedText className="text-sm text-foreground/60">
                      Email, senha, plano e exclusão de conta são da sua conta — volte para
                      Administrador no seletor para mexer neles.
                    </ThemedText>
                  </View>
                </CardContent>
              </Card>
            </Animated.View>
          )}

          {/* Bloco topo: stats + tiles (perfil público / galeria), espaçamento curto */}
          <View style={webTopBlockStyle} className={!isWeb ? 'mb-4' : undefined}>
            {/* Stats Cards */}
            <Animated.View
              entering={FadeInDown.delay(100).duration(400)}
              style={webStatsRowStyle}
              className={
                !isWeb ? `${profileStatsMb} flex-row flex-wrap ${profileStatsGap}` : undefined
              }
            >
              {stats.map((stat, index) => (
                <StatCard
                  key={stat.label}
                  icon={stat.icon}
                  label={stat.label}
                  value={stat.value}
                  color={stat.color}
                  delay={150 * (index + 1)}
                />
              ))}
            </Animated.View>

            {/* Perfil público + Galeria — dois tiles no mesmo estilo dos stats */}
            {isProvider && (
              <Animated.View
                style={webStatsRowStyle}
                className={!isWeb ? `flex-row flex-wrap ${profileStatsGap}` : undefined}
              >
                <ProfileNavTile
                  icon="storefront-outline"
                  title={
                    isActing
                      ? `Perfil de ${acting.name}`
                      : isProfessional
                        ? 'Seu perfil'
                        : 'Perfil público'
                  }
                  subtitle={
                    isActing
                      ? `Como ${acting.name} aparece em ${membership?.organizationName ?? terms.organization}`
                      : isProfessional
                        ? `Como você aparece em ${membership?.organizationName ?? terms.organization}`
                        : 'Sua vitrine de serviços'
                  }
                  onPress={() => router.push('/(tabs)/(provider)/profile/public')}
                  isWeb={isWeb}
                  delay={120}
                />
                <ProfileNavTile
                  icon="images-outline"
                  title="Galeria"
                  subtitle="Fotos dos seus trabalhos"
                  onPress={() => router.push('/(tabs)/(provider)/profile/gallery')}
                  isWeb={isWeb}
                  delay={200}
                />
              </Animated.View>
            )}
          </View>

          {/* Card de Informações */}
          <Animated.View entering={FadeInDown.delay(300).duration(400)}>
            <Card>
              <CardContent className="p-0">
                {/* Header do Card */}
                <View className="flex-row items-center justify-between gap-3 border-b border-border p-4">
                  <View className="min-w-0 flex-1 flex-row items-center gap-3">
                    <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                      <Ionicons
                        className="text-primary dark:text-white"
                        name="person-outline"
                        size={20}
                      />
                    </View>
                    <ThemedText className="min-w-0 flex-1 font-semibold text-lg leading-snug text-foreground">
                      Informações Pessoais
                    </ThemedText>
                  </View>
                  <View className="shrink-0">
                    <Button variant="ghost" size="sm" onPress={() => setShowEditModal(true)}>
                      <View className="flex-row items-center gap-2">
                        <Ionicons name="pencil-outline" size={16} color="#FD5001" />
                        <ThemedText className="text-sm text-primary">Editar</ThemedText>
                      </View>
                    </Button>
                  </View>
                </View>

                {/* Informações */}
                <View className="px-4">
                  {isActing ? null : user?.email ? (
                    <InfoRow icon="mail-outline" label="Email" value={user.email} />
                  ) : (
                    <View className="border-b border-border py-4">
                      <View className="flex-row items-center gap-3">
                        <Ionicons className="text-neutral-500" name="mail-outline" size={18} />
                        <View className="flex-1">
                          <ThemedText className="text-xs text-foreground/50">Email</ThemedText>
                          <ThemedText className="text-sm text-foreground/70">
                            Não cadastrado
                          </ThemedText>
                        </View>
                      </View>
                      <Button
                        variant="outline"
                        className="mt-3 border-primary"
                        onPress={() => setShowCredentialsModal(true)}
                      >
                        <View className="flex-row items-center gap-2">
                          <Ionicons name="key-outline" size={18} color="#FD5001" />
                          <ThemedText className="font-medium text-primary">
                            Adicionar email e senha
                          </ThemedText>
                        </View>
                      </Button>
                    </View>
                  )}

                  {isActing ? null : user?.phone ? (
                    <InfoRow icon="logo-whatsapp" label="WhatsApp" value={user.phone} />
                  ) : (
                    <View className="border-b border-border py-4">
                      <View className="flex-row items-center gap-3">
                        <Ionicons className="text-neutral-500" name="logo-whatsapp" size={18} />
                        <View className="flex-1">
                          <ThemedText className="text-xs text-foreground/50">WhatsApp</ThemedText>
                          <ThemedText className="text-sm text-foreground/70">
                            Não cadastrado
                          </ThemedText>
                        </View>
                      </View>
                      <Button
                        variant="outline"
                        className="mt-3 border-primary"
                        onPress={() => setShowWhatsappModal(true)}
                      >
                        <View className="flex-row items-center gap-2">
                          <Ionicons name="logo-whatsapp" size={18} color="#FD5001" />
                          <ThemedText className="font-medium text-primary">
                            Adicionar WhatsApp
                          </ThemedText>
                        </View>
                      </Button>
                    </View>
                  )}

                  {isProvider && (
                    <>
                      <InfoRow
                        icon="business-outline"
                        label="Nome do Negócio"
                        value={providerData?.title}
                      />
                      <InfoRow
                        icon="call-outline"
                        label="Telefone Comercial"
                        value={providerData?.businessPhone}
                      />
                      <InfoRow
                        icon="location-outline"
                        label="Endereço"
                        value={providerData?.address}
                      />
                      <InfoRow
                        icon="document-text-outline"
                        label="Sobre"
                        value={providerData?.bio}
                      />
                    </>
                  )}

                  {isClient && (
                    <>
                      <InfoRow icon="call-outline" label="Telefone" value={clientProfile?.phone} />
                      <InfoRow
                        icon="location-outline"
                        label="Endereço"
                        value={clientProfile?.address}
                      />
                      {!hasProviderProfile(user) && (
                        <View className="mt-4 border-t border-border pt-4">
                          <Button
                            variant="outline"
                            className="border-primary"
                            onPress={() => router.push('/(tabs)/(client)/become-provider')}
                          >
                            <View className="flex-row items-center gap-2">
                              <Ionicons name="briefcase-outline" size={18} color="#FD5001" />
                              <ThemedText className="font-medium text-primary">
                                Virar prestador e oferecer serviços
                              </ThemedText>
                            </View>
                          </Button>
                        </View>
                      )}
                    </>
                  )}

                  {!isActing && (
                    <InfoRow
                      icon="calendar-outline"
                      label="Membro desde"
                      value={formatDate(user?.createdAt)}
                    />
                  )}
                </View>
              </CardContent>
            </Card>
          </Animated.View>

          {/* Profissional da equipe: de quem é o negócio; plano e assinatura são do dono */}
          {isProvider && isProfessional && membership && (
            <Animated.View
              entering={FadeInDown.delay(330).duration(400)}
              className={!isWeb ? profileSectionGap : undefined}
            >
              <Card>
                <CardContent className="flex-row items-center gap-3 p-4">
                  <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                    <Ionicons
                      className="text-primary dark:text-white"
                      name="people-outline"
                      size={20}
                    />
                  </View>
                  <View className="min-w-0 flex-1">
                    <ThemedText className="text-sm text-foreground/60">
                      Você faz parte de
                    </ThemedText>
                    <ThemedText
                      numberOfLines={1}
                      className="font-semibold text-lg leading-snug text-foreground"
                    >
                      {membership.organizationName}
                    </ThemedText>
                  </View>
                </CardContent>
              </Card>
            </Animated.View>
          )}

          {/* Card Adicionar perfil de cliente (apenas Provider sem clientProfile) */}
          {/* Desativado: funcionalidade de prestador poder ser cliente. Se banco já tiver
              clientProfile em usuário prestador, prestador prevalece e front ignora esse perfil. */}
          {/* {isProvider && !hasClientProfile(user) && (
            <Animated.View entering={FadeInDown.delay(360).duration(400)} className={!isWeb ? profileSectionGap : undefined}>
              <Card>
                <CardContent className="p-0">
                  <View className="p-4">
                    <View className="mb-3 flex-row items-start gap-3">
                      <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                        <Ionicons className="text-primary dark:text-white" name="person-add-outline" size={20} />
                      </View>
                      <ThemedText className="min-w-0 flex-1 font-semibold text-lg leading-snug text-foreground">
                        Receber contratos de outros prestadores
                      </ThemedText>
                    </View>
                    <ThemedText className="text-foreground/60 mb-4 text-sm">
                      Adicione um perfil de cliente para que outros prestadores possam criar
                      contratos com você. Você aparecerá na busca ao criarem novos contratos.
                    </ThemedText>
                    <Button
                      onPress={() => setShowAddClientProfileModal(true)}
                      variant="outline"
                      className="border-primary"
                    >
                      <View className="flex-row items-center gap-2">
                        <Ionicons name="add-circle-outline" size={18} color="#FD5001" />
                        <ThemedText className="font-medium text-primary">
                          Adicionar perfil de cliente
                        </ThemedText>
                      </View>
                    </Button>
                  </View>
                </CardContent>
              </Card>
            </Animated.View>
          )} */}

          {/* Recebimento automático (T4.5, PLANO seção 6.1): oferta para quem
              está no manual, status para quem já ativou/começou — a própria
              porta já se esconde quando o recurso não se aplica (sem sobrar
              respiro vazio, por isso não usa o wrapper `Animated.View` dos
              vizinhos). */}
          {isProvider && (canManagePayment || isSingleTillNonOwner) && (
            <PaymentAccountEntryCard
              className={!isWeb ? profileSectionGap : undefined}
              singleTillNonOwner={isSingleTillNonOwner}
            />
          )}

          {/* Testar integração WhatsApp (apenas Provider, apenas em dev) */}
          {isProvider && __DEV__ && (
            <Animated.View
              entering={FadeInDown.delay(450).duration(400)}
              className={!isWeb ? profileSectionGap : undefined}
            >
              <Card>
                <CardContent className="p-4">
                  <View className="mb-3 flex-row items-start gap-3">
                    <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-premium/10 dark:bg-premium">
                      <Ionicons
                        className="text-premium dark:text-white"
                        name="chatbubble-ellipses-outline"
                        size={20}
                      />
                    </View>
                    <ThemedText className="min-w-0 flex-1 font-semibold text-lg leading-snug text-foreground">
                      Testar WhatsApp (template)
                    </ThemedText>
                  </View>
                  <ThemedText className="mb-3 text-sm text-foreground/70">
                    Envia uma mensagem de teste para o número e template informados (padrão:
                    hello_world). Você pode opcionalmente informar parâmetros para preencher o corpo
                    do template.
                  </ThemedText>
                  <View className="flex-row items-end gap-2">
                    <View className="flex-1">
                      <ThemedText className="mb-1 text-xs text-foreground/60">
                        Número (ex.: 11999999999)
                      </ThemedText>
                      <TextInput
                        value={whatsappTestPhone}
                        onChangeText={setWhatsappTestPhone}
                        placeholder="11999999999"
                        placeholderTextColor="#a1a1aa"
                        keyboardType="phone-pad"
                        className="rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                        editable={!whatsappTestLoading}
                      />
                    </View>
                    <View className="flex-1">
                      <ThemedText className="mb-1 text-xs text-foreground/60">
                        Template (ex.: hello_world)
                      </ThemedText>
                      <TextInput
                        value={whatsappTestTemplate}
                        onChangeText={setWhatsappTestTemplate}
                        placeholder="hello_world"
                        placeholderTextColor="#a1a1aa"
                        autoCapitalize="none"
                        className="rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                        editable={!whatsappTestLoading}
                      />
                    </View>
                  </View>

                  {/* Parâmetros dinâmicos */}
                  <View className="mt-4 gap-2">
                    <View className="mb-1 flex-row items-center justify-between">
                      <ThemedText className="text-xs text-foreground/70">
                        Parâmetros do template (opcional)
                      </ThemedText>
                      <Pressable
                        onPress={() =>
                          setWhatsappParams((prev) => [...prev, { name: '', value: '' }])
                        }
                        disabled={whatsappTestLoading}
                      >
                        <View className="flex-row items-center gap-1">
                          <Ionicons name="add-circle-outline" size={16} color="#AF340B" />
                          <ThemedText className="text-xs text-premium">Adicionar</ThemedText>
                        </View>
                      </Pressable>
                    </View>
                    {whatsappParams.map((param, index) => (
                      <View key={index} className="flex-row items-end gap-2">
                        <View className="w-24">
                          <ThemedText className="mb-1 text-xs text-foreground/60">Nome</ThemedText>
                          <TextInput
                            value={param.name}
                            onChangeText={(text) => {
                              setWhatsappParams((prev) => {
                                const copy = [...prev];
                                copy[index] = { ...copy[index], name: text };
                                return copy;
                              });
                            }}
                            placeholder="1, nome, amount..."
                            placeholderTextColor="#a1a1aa"
                            autoCapitalize="none"
                            className="rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                            editable={!whatsappTestLoading}
                          />
                        </View>
                        <View className="flex-1">
                          <ThemedText className="mb-1 text-xs text-foreground/60">Valor</ThemedText>
                          <TextInput
                            value={param.value}
                            onChangeText={(text) => {
                              setWhatsappParams((prev) => {
                                const copy = [...prev];
                                copy[index] = { ...copy[index], value: text };
                                return copy;
                              });
                            }}
                            placeholder="João, R$ 100,00..."
                            placeholderTextColor="#a1a1aa"
                            className="rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                            editable={!whatsappTestLoading}
                          />
                        </View>
                        <Pressable
                          onPress={() =>
                            setWhatsappParams((prev) =>
                              prev.filter((_, paramIndex) => paramIndex !== index),
                            )
                          }
                          disabled={whatsappTestLoading}
                          className="mb-1 h-10 w-10 items-center justify-center rounded-full border border-border bg-background"
                        >
                          <Ionicons className="text-error-500" name="trash-outline" size={16} />
                        </Pressable>
                      </View>
                    ))}
                  </View>

                  {/* Parâmetro para botão URL */}
                  <View className="mt-4">
                    <ThemedText className="mb-1 text-xs text-foreground/60">
                      Parâmetro do botão (URL) - opcional
                    </ThemedText>
                    <TextInput
                      value={whatsappButtonParam}
                      onChangeText={setWhatsappButtonParam}
                      placeholder="ex.: https://meusite.com/pedido/123"
                      placeholderTextColor="#a1a1aa"
                      autoCapitalize="none"
                      className="rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                      editable={!whatsappTestLoading}
                    />
                  </View>

                  <View className="mt-4 flex-row justify-end">
                    <Button
                      onPress={async () => {
                        const to = whatsappTestPhone.trim();
                        const templateName = whatsappTestTemplate.trim() || 'hello_world';
                        if (!to) {
                          addToast('Informe o número para envio do teste.', 'error');
                          return;
                        }
                        // Montar objeto de variáveis ignorando nomes vazios
                        const variables = whatsappParams.reduce<Record<string, string>>(
                          (acc, param) => {
                            const key = param.name.trim();
                            if (!key) return acc;
                            acc[key] = param.value;
                            return acc;
                          },
                          {},
                        );

                        setWhatsappTestLoading(true);
                        try {
                          await api.post('/provider/whatsapp-test', {
                            to,
                            templateName,
                            variables: Object.keys(variables).length ? variables : undefined,
                            buttonUrlParam: whatsappButtonParam.trim() || undefined,
                          });
                          addToast(
                            `Mensagem de teste enviada com o template "${templateName}". Confira o WhatsApp.`,
                            'success',
                          );
                          setWhatsappTestPhone('');
                          setWhatsappParams([]);
                        } catch (e: unknown) {
                          addToast(getApiErrorMessage(e, 'Falha ao enviar teste.'), 'error');
                        } finally {
                          setWhatsappTestLoading(false);
                        }
                      }}
                      disabled={whatsappTestLoading}
                    >
                      {whatsappTestLoading ? (
                        <ActivityIndicator size="small" color="#fff" />
                      ) : (
                        <View className="flex-row items-center gap-2">
                          <Ionicons name="send-outline" size={18} color="#fff" />
                          <ThemedText className="font-medium text-white">Enviar teste</ThemedText>
                        </View>
                      )}
                    </Button>
                  </View>
                </CardContent>
              </Card>
            </Animated.View>
          )}

          {/* Card WhatsApp Connect desativado: envio agora sempre sai pelo número
              da própria ControlaPag, prestador não precisa mais conectar o dele. */}
          {/* {isProvider && FEATURES.WHATSAPP_PROVIDER === 'evolution' && (
            <Animated.View entering={FadeInDown.delay(460).duration(400)} className={!isWeb ? profileSectionGap : undefined}>
              <WhatsappConnectCard testPhone={providerData?.businessPhone ?? undefined} />
            </Animated.View>
          )} */}

          {/* Gerenciar modelos de notificação WhatsApp (apenas Provider, apenas em dev) */}
          {isProvider && __DEV__ && (
            <Animated.View
              entering={FadeInDown.delay(480).duration(400)}
              className={!isWeb ? profileSectionGap : undefined}
            >
              <WhatsappTemplatesManager />
            </Animated.View>
          )}

          {/* Prestador: plano, aparência, conta e o resto moram em Configurações. */}
          {isProvider && (
            <Animated.View
              entering={FadeInDown.delay(482).duration(400)}
              className={isWeb ? 'mb-4' : `mb-4 ${profileSectionGap}`}
            >
              <Card>
                <CardContent className="p-0">
                  <Pressable
                    onPress={() => router.push('/(tabs)/(provider)/settings')}
                    accessibilityRole="button"
                    className="flex-row items-center gap-3 p-4"
                  >
                    <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                      <Ionicons
                        className="text-primary dark:text-white"
                        name="settings-outline"
                        size={20}
                      />
                    </View>
                    <View className="min-w-0 flex-1">
                      <ThemedText className="font-semibold text-lg leading-snug text-foreground">
                        Configurações
                      </ThemedText>
                      <ThemedText className="text-sm text-foreground/60">
                        Plano, aparência, dados para pagar e sua conta.
                      </ThemedText>
                    </View>
                    <Ionicons className="text-neutral-400" name="chevron-forward" size={20} />
                  </Pressable>
                </CardContent>
              </Card>
            </Animated.View>
          )}

          {/* Cliente não tem Configurações: aparência e conta seguem aqui. */}
          {!isProvider && (
            <>
              {/* Aparência: claro, escuro ou igual ao aparelho */}
              <Animated.View
                entering={FadeInDown.delay(482).duration(400)}
                className={!isWeb ? profileSectionGap : undefined}
              >
                <AppearanceCard />
              </Animated.View>

              {/* Botão de Logout */}
              <Animated.View
                entering={FadeInDown.delay(500).duration(400)}
                className={isWeb ? 'mb-4' : `mb-4 ${profileFooterGap}`}
              >
                <Button
                  variant="outline"
                  size="lg"
                  onPress={() => useAuthStore.getState().logout()}
                  className="border-error-200"
                >
                  <View className="flex-row items-center gap-2">
                    <Ionicons className="text-error-500" name="log-out-outline" size={20} />
                    <ThemedText className="font-medium text-error-500">Sair da Conta</ThemedText>
                  </View>
                </Button>
              </Animated.View>

              {!isActing && (
                <Animated.View entering={FadeInDown.delay(520).duration(400)} className="mb-4">
                  <Button
                    variant="ghost"
                    size="lg"
                    onPress={handleDeleteAccount}
                    disabled={isDeletingAccount}
                  >
                    <View className="flex-row items-center gap-2">
                      <Ionicons className="text-error-500" name="trash-outline" size={18} />
                      <ThemedText className="font-medium text-sm text-error-500">
                        {isDeletingAccount ? 'Excluindo...' : 'Excluir conta'}
                      </ThemedText>
                    </View>
                  </Button>
                </Animated.View>
              )}
            </>
          )}
        </View>
      </DashboardScrollView>

      {/* Modal de Edição */}
      <EditProfileModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        onSuccess={() => {
          refetchProvider();
          setShowEditModal(false);
        }}
      />

      <AddCredentialsModal
        open={showCredentialsModal}
        onDismiss={() => setShowCredentialsModal(false)}
      />

      <AddWhatsappModal open={showWhatsappModal} onDismiss={() => setShowWhatsappModal(false)} />

      {/* Modal Adicionar perfil de cliente — desativado: prestador poder ser cliente */}
      {/* <AddClientProfileModal
        open={showAddClientProfileModal}
        onOpenChange={setShowAddClientProfileModal}
        onSuccess={() => refetchProvider()}
      /> */}

      {/* Modal de exclusão de conta */}
      <Dialog
        open={showDeleteAccountModal}
        onOpenChange={setShowDeleteAccountModal}
        dismissable={!isDeletingAccount}
      >
        <DialogContent className="rounded-2xl">
          <DialogHeader className="gap-2">
            <View className="mb-1 h-12 w-12 items-center justify-center rounded-full bg-error-50 dark:bg-error-500">
              <Ionicons className="text-error-500 dark:text-white" name="trash-outline" size={22} />
            </View>
            <DialogTitle>Excluir conta</DialogTitle>
            <DialogDescription>
              Isso apaga sua conta e todos os dados associados. Não dá para desfazer.
              {Platform.OS === 'ios'
                ? ' Se você assina pela App Store, cancele a assinatura em Ajustes > Apple ID > Assinaturas — excluir a conta não cancela a cobrança da Apple.'
                : Platform.OS === 'android'
                  ? ' Se você assina pelo Google Play, cancele a assinatura em Google Play > Pagamentos e assinaturas. Excluir a conta não cancela a cobrança do Google.'
                  : ''}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-6 flex-row gap-2">
            <Button
              variant="outline"
              size="md"
              disabled={isDeletingAccount}
              onPress={() => setShowDeleteAccountModal(false)}
              className="flex-1"
            >
              <ThemedText className="font-semibold text-sm">Cancelar</ThemedText>
            </Button>
            <Button
              variant="destructive"
              size="md"
              disabled={isDeletingAccount}
              onPress={confirmDeleteAccount}
              className="flex-1"
            >
              <View className="flex-row items-center gap-2">
                {isDeletingAccount ? <ActivityIndicator size="small" color="#fff" /> : null}
                <ThemedText className="font-semibold text-sm text-white">
                  {isDeletingAccount ? 'Excluindo...' : 'Excluir conta'}
                </ThemedText>
              </View>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChoiceDialog
        open={avatarSourceOpen}
        onOpenChange={setAvatarSourceOpen}
        title="Alterar foto"
        options={[
          { label: 'Galeria', onPress: () => void pickImage() },
          { label: 'Câmera', onPress: () => void takePhoto() },
        ]}
      />
    </View>
  );
}
